import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import * as Papa from 'papaparse';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';


export interface FileUploadColumn {
  name: string;
  required: boolean;
  validationRegex?: RegExp;
  validationMessage?: string;
  serverValidationMessage?: string;
  }

export interface FileUploadDialogData {
  columns: FileUploadColumn[];
  title?: string;
  maxRows?: number;
  notAllowedChars?: string[];
  serverValidateFn?: (fileData: string, fileName: string) => Observable<Record<string, string[]>>;
  importFn?: (fData: string, fileName: string, colsInFile: string) => Observable<any>;
}

export interface FileUploadDialogResult {
  headers: string[];
  rows: string[][];
  extraColumns: string[];
}

type DelimiterOption = 'auto' | ',' | ';' | '\t' | '|';
type EncodingOption = 'auto' | 'UTF-8' | 'ISO-8859-1' | 'Windows-1252' | 'UTF-16';

@Component({
  selector: 'app-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
  standalone: false
})
export class FileUploadDialogComponent {

  delimiterOptions: { value: DelimiterOption; label: string }[] = [
    { value: 'auto', label: 'Auto' },
    { value: ',', label: 'Comma (,)' },
    { value: ';', label: 'Semicolon (;)' },
    { value: '\t', label: 'Tab' },
    { value: '|', label: 'Pipe (|)' }
  ];

  encodingOptions: { value: EncodingOption; label: string }[] = [
    { value: 'auto', label: 'Auto' },
    { value: 'UTF-8', label: 'UTF-8' },
    { value: 'ISO-8859-1', label: 'ISO-8859-1' },
    { value: 'Windows-1252', label: 'Windows-1252' },
    { value: 'UTF-16', label: 'UTF-16' }
  ];

  selectedDelimiter: DelimiterOption = 'auto';
  selectedEncoding: EncodingOption = 'auto';

  fileName = '';
  parsedHeaders: string[] = [];
  parsedRows: string[][] = [];
  extraColumns: string[] = [];
  missingRequiredColumns: string[] = [];
  parseError = '';
  isDragOver = false;
  totalRowCount = 0;
  hasInvalidChars = false;
  hasServerErrors = false;
  isValidating = false;
  private invalidCellMap = new Set<string>();
  private serverErrorCellMap = new Set<string>();
  private validationMessageMap = new Map<string, string>();
  private serverValidationMessageMap = new Map<string, string>();
  private selectedFile: File | null = null;

  private static readonly DEFAULT_NOT_ALLOWED_CHARS = ['\\', '"', '/', '|', '^','£', '$', '%', '¤'];

  file = { file: { fileData: {}}};




  constructor(
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    public dialogRef: MatDialogRef<FileUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FileUploadDialogData
  ) {}

  get title(): string {
    return this.data.title || 'Import File';
  }

  get maxRows(): number {
    return this.data.maxRows ?? 100;
  }

  get notAllowedChars(): string[] {
    return this.data.notAllowedChars ?? FileUploadDialogComponent.DEFAULT_NOT_ALLOWED_CHARS;
  }

  get expectedColumnNames(): string[] {
    return this.data.columns.map(c => c.name);
  }

  get validationMessages(): string[] {
    return [...new Set(this.validationMessageMap.values())];
  }

  get serverValidationMessages(): string[] {
    return [...new Set(this.serverValidationMessageMap.values())];
  }

  isExtraColumn(header: string): boolean {
    return this.extraColumns.includes(header);
  }

  isCellInvalid(rowIndex: number, colIndex: number): boolean {
    return this.invalidCellMap.has(`${rowIndex}:${colIndex}`);
  }

  isCellServerError(rowIndex: number, colIndex: number): boolean {
    return this.serverErrorCellMap.has(`${rowIndex}:${colIndex}`);
  }

  getInvalidCharsInCell(cell: string): string[] {
    if (!cell) return [];
    return this.notAllowedChars.filter(ch => cell.includes(ch));
  }

  getColumnDef(colIndex: number): FileUploadColumn | undefined {
    const header = this.parsedHeaders[colIndex];
    if (!header) return undefined;
    return this.data.columns.find(c => c.name.toLowerCase() === header.toLowerCase());
  }

  getCellTooltip(ri: number, ci: number, cell: string): string {
    const key = `${ri}:${ci}`;
    const parts: string[] = [];
    if (this.isCellInvalid(ri, ci)) {
      parts.push(this.validationMessageMap.get(key) || 'Invalid value');
    }
    if (this.isCellServerError(ri, ci)) {
      parts.push(this.serverValidationMessageMap.get(key) || 'Server validation error');
    }
    return parts.join('. ');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv' && ext !== 'txt') {
      this.parseError = 'Only .csv and .txt files are supported.';
      return;
    }

    this.selectedFile = file;
    this.fileName = file.name;
    this.parseFile();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.selectedFile = input.files[0];
    this.fileName = this.selectedFile.name;
    this.parseFile();
  }

  onDelimiterChange(): void {
    if (this.selectedFile) {
      this.parseFile();
    }
  }

  onEncodingChange(): void {
    if (this.selectedFile) {
      this.parseFile();
    }
  }

  private parseFile(): void {
    if (!this.selectedFile) return;

    this.parseError = '';
    this.parsedHeaders = [];
    this.parsedRows = [];
    this.extraColumns = [];
    this.missingRequiredColumns = [];
    this.totalRowCount = 0;
    this.hasInvalidChars = false;
    this.hasServerErrors = false;
    this.invalidCellMap.clear();
    this.serverErrorCellMap.clear();
    this.validationMessageMap.clear();
    this.serverValidationMessageMap.clear();

    const encoding = this.selectedEncoding === 'auto' ? undefined : this.selectedEncoding;

    Papa.parse(this.selectedFile, {
      header: true,
      skipEmptyLines: true,
      delimiter: this.selectedDelimiter === 'auto' ? '' : this.selectedDelimiter,
      encoding,

      complete: (results) => {
        if (results.errors?.length) {
          const fatal = results.errors.find(e => (e.type as string) === 'Abort');
          if (fatal) {
            this.parseError = fatal.message;
            return;
          }
        }

        const fields = results.meta?.fields;
        if (!fields?.length) {
          this.parseError = 'The file appears to be empty or has no header row.';
          return;
        }

        this.parsedHeaders = fields;
        const allDataRows = (results.data as Record<string, string>[]).map(
          row => this.parsedHeaders.map(h => row[h] ?? '')
        );
        this.totalRowCount = allDataRows.length;

        if (this.totalRowCount > this.maxRows) {
          this.parseError = `The file contains ${this.totalRowCount} rows, which exceeds the maximum allowed (${this.maxRows}).`;
          return;
        }

        this.parsedRows = allDataRows;
        this.file.file.fileData = results.data;

        if (this.data.serverValidateFn) {
          this.runServerValidation();
        }

        const expectedLower = this.data.columns.map(c => c.name.toLowerCase());
        this.extraColumns = this.parsedHeaders.filter(
          h => !expectedLower.includes(h.toLowerCase())
        );

        const headersLower = this.parsedHeaders.map(h => h.toLowerCase());
        this.missingRequiredColumns = this.data.columns
          .filter(c => c.required && !headersLower.includes(c.name.toLowerCase()))
          .map(c => c.name);

        this.parsedRows.forEach((row, ri) => {
          row.forEach((cell, ci) => {
            if (!cell) return;

            const colDef = this.getColumnDef(ci);

            if (colDef?.validationRegex) {
              const regex = colDef.validationRegex;
              regex.lastIndex = 0;
              const isInvalid = regex.global ? regex.test(cell) : !regex.test(cell);
              if (isInvalid) {
                this.invalidCellMap.add(`${ri}:${ci}`);
                this.validationMessageMap.set(`${ri}:${ci}`, colDef.validationMessage || 'Invalid value');
                this.hasInvalidChars = true;
              }
            } else if (this.notAllowedChars.some(ch => cell.includes(ch))) {
              this.invalidCellMap.add(`${ri}:${ci}`);
              this.validationMessageMap.set(`${ri}:${ci}`,
                'Contains not-allowed characters: ' + this.getInvalidCharsInCell(cell).join(' '));
              this.hasInvalidChars = true;
            }
          });
        });
      },
      error: (err) => {
        this.parseError = err.message || 'Failed to parse the file.';
      }
    });
  }

  private runServerValidation(): void {
    const fData = JSON.stringify(this.file);
    this.isValidating = true;
    this.serverErrorCellMap.clear();
    this.hasServerErrors = false;

    this.data.serverValidateFn!(fData, this.fileName).subscribe({
      next: (errors) => {
        this.isValidating = false;
        if (!errors || typeof errors !== 'object') return;

        const headersLower = this.parsedHeaders.map(h => h.toLowerCase());

        Object.keys(errors).forEach(colName => {
          const colIndex = headersLower.indexOf(colName.toLowerCase());
          if (colIndex === -1) return;

          const errorValues = errors[colName];
          if (!Array.isArray(errorValues)) return;

          const errorSet = new Set(errorValues.map(v => String(v).toLowerCase()));
          const colDef = this.getColumnDef(colIndex);
          const serverMsg = colDef?.serverValidationMessage || 'Server validation error';

          this.parsedRows.forEach((row, ri) => {
            const cellValue = row[colIndex];
            if (cellValue != null && errorSet.has(String(cellValue).toLowerCase())) {
              this.serverErrorCellMap.add(`${ri}:${colIndex}`);
              this.serverValidationMessageMap.set(`${ri}:${colIndex}`, serverMsg);
              this.hasServerErrors = true;
            }
          });
        });
      },
      error: () => {
        this.isValidating = false;
        this.parseError = 'Server validation failed. Please try again.';
      }
    });
  }

  confirm(): void {

    const dialogRef2 = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: this.i18n.transform('users:shared_editConfirm'),
        additionalText:  this.i18n.transform('users:users_import_updateUserswarning'),
        showTitleInHeader: true,
        confirmText: this.i18n.transform('Yes'),
        cancelText: this.i18n.transform('No'),
      }
    });
    dialogRef2.afterClosed().subscribe(confirm => {
      if (confirm){
        const fData = JSON.stringify(this.file);
        this.data.importFn!(fData, this.fileName, this.parsedHeaders.join(',')).subscribe({
          next: (response) => {
            if (response.ok) {
              this.dialogRef.close(response);
            } else {
              const errCode = response.returnObject?.errCode;
              const errUsers = response.returnObject?.errUsers?.map(u => u.user_name || '') || [];
              if (errCode == 20000 || errCode == 50000) {
                this.parseError = this.i18n.transform('users:users_err_importuserError').replace('{0}', errUsers.join(', '));
              } else if (errCode == 50522) {
                this.parseError = this.i18n.transform('users:users_err_maxuserlimitError');
              } else if (errCode == 50154 || errCode == 50506) {
                let error = this.i18n.transform('users:users_err_retrynouserError');
                if (errUsers.length) {
                  error += ' ' + this.i18n.transform('users:users_err_importuserError').replace('{0}', errUsers.join(', '));
                }
                this.parseError = error;
              } else {
                this.parseError = 'This file was not imported correctly';
              }
            }
          },
          error: (error) => {
            this.parseError = 'This file was not imported correctly';
          }
        });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

    openHelp(): void {
      this.dialog.open(DialogHelpComponent, {
        panelClass: 'dialog-help-pannel',
        backdropClass: 'backdrop-invisible',
        width: '700px',
        data: {
          title: this.i18n.transform('users:users_help_title_updateSimpleUsers'),
          text: this.i18n.transform('users:users_help_updateSimpleUsers'),
          renderAsHtml: true
        }
      });
    }
}
